import { useCallback, useEffect, useRef, useState } from 'react';
/**
 * A generic storage hook that works with any storage adapter (localStorage, sessionStorage, etc.).
 *
 * @template T - The type of the stored value
 * @param {string} key - The storage key
 * @param {T | (() => T)} initialValue - The initial value if no value exists in storage
 * @param {StorageAdapter} storage - The storage adapter (SafeLocalStorage or SafeSessionStorage)
 * @param {StorageOptions} options - `broadcast` (default true) and `global` (default false)
 * @returns {Storage<T>} A tuple containing the current value, a setter function, and a getter function
 */
export const useStorage = (key, initialValue, storage, options = {}) => {
    const keyRef = useRef(key); // Key must not change after initial render
    const initialValueRef = useRef(initialValue); // Initial value must not change after initial render
    const storageRef = useRef(storage); // Storage adapter must not change after initial render
    const optionsRef = useRef(options); // Options must not change after initial render
    const parsedValueRef = useRef(undefined);
    const readValue = useCallback(() => {
        const item = storageRef.current.getItem(keyRef.current, optionsRef.current);
        if (item === null) {
            return initialValueRef.current instanceof Function
                ? initialValueRef.current()
                : initialValueRef.current;
        }
        // Still read storage on every call so updaters see other hooks' writes. Reuse
        // the parsed object so returning prev can actually bail out of a state update.
        if (parsedValueRef.current?.item === item) {
            return parsedValueRef.current.value;
        }
        try {
            const value = JSON.parse(item);
            parsedValueRef.current = { item, value };
            return value;
        }
        catch {
            return initialValueRef.current instanceof Function
                ? initialValueRef.current()
                : initialValueRef.current;
        }
    }, []);
    const [storedValue, setStoredValue] = useState(readValue);
    const setValue = useCallback((value) => {
        setStoredValue(() => {
            const prev = readValue();
            const newValue = value instanceof Function ? value(prev) : value;
            if (newValue !== prev) {
                storageRef.current.setItem(keyRef.current, JSON.stringify(newValue), optionsRef.current);
                window.dispatchEvent(new CustomEvent('storage-change', {
                    detail: { key: keyRef.current },
                }));
            }
            return newValue;
        });
    }, [readValue]);
    const handleChange = useCallback((event) => {
        const customEvent = event;
        if (customEvent.detail?.key !== keyRef.current)
            return;
        // RAF so that the event is processed after React finishes rendering,
        // otherwise an error can be thrown when the state changes during render
        window.requestAnimationFrame(() => setStoredValue(readValue()));
    }, [readValue]);
    useEffect(() => {
        if (optionsRef.current.broadcast === false)
            return; // broadcast defaults to true
        window.addEventListener('storage-change', handleChange);
        return () => window.removeEventListener('storage-change', handleChange);
    }, [handleChange]);
    return [storedValue, setValue, readValue];
};
//# sourceMappingURL=useStorage.js.map